import { useState } from 'react';
import { MapContainer } from '@/components/map/MapContainer';
import { Header } from '@/components/layout/Header';
import { QueryResultPanel } from '@/components/map/QueryResultPanel';
import { SearchBox } from '@/components/map/SearchBox';
import { MeasureTools, formatDistance, formatArea } from '@/components/map/MeasureTools';
import { PaymentModal } from '@/components/payment/PaymentModal';
import { CompareModal } from '@/components/map/CompareModal';
import { useAuthStore } from '@/stores/authStore';
import type { SpatialQueryResult } from '@/api/mapApi';
import type { Map as MaplibreMap } from 'maplibre-gl';

interface MeasureResult {
  type: 'distance' | 'area';
  value: number;
  points: [number, number][];
}

export function HomePage() {
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [queryResult, setQueryResult] = useState<SpatialQueryResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [mapInstance, setMapInstance] = useState<MaplibreMap | null>(null);
  const [measureMode, setMeasureMode] = useState<'distance' | 'area' | null>(null);
  const [measureResult, setMeasureResult] = useState<MeasureResult | null>(null);
  const [showPayment, setShowPayment] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());

  const canSeeDetails = isAuthenticated;  // simplified tier check

  return (
    <div className="h-full flex flex-col">
      <Header />

      {/* Search bar overlay */}
      <div className="absolute top-16 left-4 right-4 md:right-auto md:w-96 z-20">
        <SearchBox
          onSelect={(loc) => {
            setSelectedLocation({ lat: loc.lat, lng: loc.lng });
            if (mapInstance) {
              mapInstance.flyTo({ center: [loc.lng, loc.lat], zoom: 17, duration: 1500 });
              // Trigger spatial query manually
              setLoading(true);
              import('@/api/mapApi').then(async ({ spatialApi }) => {
                try {
                  const result = await spatialApi.query(loc.lat, loc.lng);
                  setQueryResult(result);
                } catch (err) {
                  console.error(err);
                  setQueryResult(null);
                } finally {
                  setLoading(false);
                }
              });
            }
          }}
        />
      </div>

      <div className="flex-1 relative">
        <MapContainer
          onMapClick={(lat, lng) => setSelectedLocation({ lat, lng })}
          onResults={setQueryResult}
          onLoadingChange={setLoading}
          onMapReady={setMapInstance}
        />

        {/* Measure tools (headless — just attaches to map) */}
        <MeasureTools
          map={mapInstance}
          active={measureMode}
          onResult={setMeasureResult}
        />

        {/* Tools panel (top right) */}
        <div className="absolute top-20 right-4 z-10 flex flex-col gap-2">
          <button
            onClick={() => setMeasureMode(measureMode === 'distance' ? null : 'distance')}
            className={`bg-white shadow-md rounded-md px-3 py-2 text-sm hover:bg-gray-50 ${
              measureMode === 'distance' ? 'ring-2 ring-brand-500' : ''
            }`}
            title="Đo khoảng cách"
          >
            📏 Khoảng cách
          </button>
          <button
            onClick={() => setMeasureMode(measureMode === 'area' ? null : 'area')}
            className={`bg-white shadow-md rounded-md px-3 py-2 text-sm hover:bg-gray-50 ${
              measureMode === 'area' ? 'ring-2 ring-brand-500' : ''
            }`}
            title="Đo diện tích"
          >
            📐 Diện tích
          </button>
          {isAuthenticated && (
            <button
              onClick={() => setShowCompare(true)}
              className="bg-white shadow-md rounded-md px-3 py-2 text-sm hover:bg-gray-50"
              title="So sánh quy hoạch"
            >
              🔄 So sánh
            </button>
          )}
          {measureResult && (
            <div className="bg-white shadow-md rounded-md p-3 text-sm">
              <p className="text-xs text-gray-500">{measureResult.type === 'distance' ? 'Khoảng cách' : 'Diện tích'}</p>
              <p className="text-lg font-bold text-gray-900">
                {measureResult.type === 'distance'
                  ? formatDistance(measureResult.value)
                  : formatArea(measureResult.value)}
              </p>
              <button
                onClick={() => {
                  setMeasureMode(null);
                  setMeasureResult(null);
                }}
                className="text-xs text-red-600 hover:underline mt-1"
              >
                Xóa
              </button>
            </div>
          )}
        </div>

        {/* Loading indicator */}
        {loading && (
          <div className="absolute top-20 right-4 md:right-32 bg-white shadow-lg rounded-lg px-4 py-2 text-sm z-10">
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-600"></div>
              <span>Đang tìm quy hoạch...</span>
            </div>
          </div>
        )}

        {/* Result panel */}
        {queryResult && selectedLocation && (
          <QueryResultPanel
            result={queryResult}
            location={selectedLocation}
            onClose={() => setQueryResult(null)}
          />
        )}

        {/* Empty state */}
        {!queryResult && !loading && !measureResult && (
          <div className="absolute top-32 left-4 md:left-4 md:top-32 md:ml-96 md:max-w-xs bg-white/95 backdrop-blur shadow-lg rounded-lg p-4 max-w-xs z-10">
            <p className="text-sm font-medium text-gray-900 mb-1">👆 Chạm vào bản đồ</p>
            <p className="text-xs text-gray-600 mb-3">
              Hoặc tìm kiếm địa chỉ ở thanh trên để xem quy hoạch & thông tin thửa đất.
            </p>
            {!canSeeDetails && (
              <button
                onClick={() => selectedLocation && setShowPayment(true)}
                disabled={!selectedLocation}
                className="w-full bg-amber-100 text-amber-900 text-xs px-3 py-2 rounded hover:bg-amber-200 disabled:opacity-50"
              >
                ☕ Ủng hộ 10.000đ xem chi tiết
              </button>
            )}
          </div>
        )}
      </div>

      {/* Payment modal */}
      {showPayment && selectedLocation && (
        <PaymentModal
          context={selectedLocation}
          onClose={() => setShowPayment(false)}
          onSuccess={() => {
            setShowPayment(false);
            if (selectedLocation) {
              setLoading(true);
              import('@/api/mapApi').then(async ({ spatialApi }) => {
                try {
                  const result = await spatialApi.query(selectedLocation.lat, selectedLocation.lng);
                  setQueryResult(result);
                } catch {
                } finally {
                  setLoading(false);
                }
              });
            }
          }}
        />
      )}

      {/* Compare modal */}
      {showCompare && <CompareModal onClose={() => setShowCompare(false)} />}
    </div>
  );
}
